import { Ico } from '@/ui/Ico'
import { cls } from '@/ui/cls'
import ProfileMenu from './ProfileMenu'
import OpenedTodayMenu from './OpenedTodayMenu'
import type { AdminDest } from '@/shared/routes'
import { canOpenManage, type Me } from '@/shared/api/session'
import type { View } from '@/shared/routes'
import { BrandMark } from '@/shared/white-label/BrandMark'
import { BrandPreviewBadge } from '@/shared/white-label/BrandPreviewBadge'
import { useActiveBrand } from '@/shared/white-label/useBrand'
import { useCanEdit, writeControlProps } from '@/shared/write-access'

interface TopbarProps {
  mode: View
  setMode: (m: View) => void
  notesOpen: boolean
  setNotesOpen: (updater: (o: boolean) => boolean) => void
  me: Me | undefined
  adminDest: AdminDest
  setAdminDest: (d: AdminDest) => void
  // Present only when the user may add leads (edit_contacts). Renders the global
  // "+ Add lead" button so a lead can be added from any view, including Focus.
  onAddLead?: () => void
  // Opens a lead overlay by company id — powers the "Opened today" trace-back menu.
  onOpenLead?: (id: string) => void
}

// label doubles as the accessible name — the visible <span> hides below
// 900px (see the max-[900px]:hidden span below), so each item still needs
// one that survives: the button's own aria-label covers that case.
const VIEWS: { value: View; label: string; icon: React.ReactNode }[] = [
  { value: 'focus', label: 'Focus', icon: Ico.flame },
  { value: 'table', label: 'Tabell', icon: Ico.table },
  { value: 'dashboard', label: 'Översikt', icon: Ico.chart },
  { value: 'map', label: 'Karta', icon: Ico.pin },
]

export default function Topbar({
  mode,
  setMode,
  notesOpen,
  setNotesOpen,
  me,
  adminDest,
  setAdminDest,
  onAddLead,
  onOpenLead,
}: TopbarProps) {
  const brand = useActiveBrand()
  const canEdit = useCanEdit()
  return (
    <header className="topbar">
      <div className="flex items-center gap-3 justify-self-start">
        <BrandMark />
        <span className="max-[900px]:hidden font-display text-[10px] tracking-[0.14em] uppercase text-ink-500 border-l border-ink-200 pl-3">{brand.product}</span>
        <BrandPreviewBadge />
      </div>

      <nav aria-label="Vy" className="inline-flex gap-1">
        {VIEWS.map(v => (
          <button
            key={v.value}
            type="button"
            onClick={() => setMode(v.value)}
            title={v.label}
            aria-label={v.label}
            className={cls(
              'inline-flex items-center gap-2 px-3.5 max-[900px]:px-2.5 py-1.5 bg-transparent cursor-pointer',
              'text-[13px] font-semibold border-b-2 transition-colors [&_svg]:w-[13px] [&_svg]:h-[13px]',
              mode === v.value ? 'text-ink-1000 border-ink-1000' : 'text-ink-500 border-transparent hover:text-ink-1000',
            )}
          >
            {v.icon}<span className="max-[900px]:hidden">{v.label}</span>
          </button>
        ))}
        {canOpenManage(me) && (
          <button
            type="button"
            onClick={() => setMode('manage')}
            title="Hantera"
            aria-label="Hantera"
            aria-current={mode === 'manage' ? 'page' : undefined}
            className={cls(
              'inline-flex items-center gap-2 px-3.5 max-[900px]:px-2.5 py-1.5 bg-transparent cursor-pointer',
              'text-[13px] font-semibold border-b-2 transition-colors',
              mode === 'manage' ? 'text-ink-1000 border-ink-1000' : 'text-ink-500 border-transparent hover:text-ink-1000',
            )}
          >
            <span>Hantera</span>
          </button>
        )}
      </nav>

      {/* Below 1360px the centred nav leaves this column too little room for
          every label, so the add button shortens to "Ny lead" and the gaps
          tighten. Labels never wrap; below 900px they go icon-only. */}
      <div className="flex items-center gap-3 max-[1360px]:gap-2 justify-self-end">
        {onOpenLead && <OpenedTodayMenu onOpenLead={onOpenLead} />}
        {onAddLead && (
          <button
            type="button"
            onClick={onAddLead}
            {...writeControlProps(canEdit, 'Lägg till en lead i CRM:et')}
            aria-label="Lägg till lead"
            className={cls(
              'inline-flex items-center gap-1.5 h-[30px] px-2.5 border cursor-pointer whitespace-nowrap',
              'text-xs font-medium rounded-[2px] transition-colors [&_svg]:w-3.5 [&_svg]:h-3.5',
              'bg-transparent border-ink-200 text-ink-700 hover:text-ink-1000 hover:border-ink-1000',
              'disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:text-ink-700 disabled:hover:border-ink-200',
            )}
          >
            {Ico.plus}
            <span className="max-[1360px]:hidden">Lägg till lead</span>
            <span className="hidden min-[900px]:max-[1360px]:inline">Ny lead</span>
          </button>
        )}
        {mode === 'focus' && (
          <button
            type="button"
            onClick={() => setNotesOpen(o => !o)}
            title={notesOpen ? 'Dölj aktivitetspanelen' : 'Visa aktivitetspanelen'}
            aria-label={notesOpen ? 'Dölj aktivitetspanelen' : 'Visa aktivitetspanelen'}
            aria-pressed={notesOpen}
            className={cls(
              'inline-flex items-center gap-1.5 h-[30px] px-2.5 border cursor-pointer whitespace-nowrap',
              'text-xs font-medium rounded-[2px] transition-colors [&_svg]:w-3.5 [&_svg]:h-3.5',
              notesOpen
                ? 'bg-brand border-brand text-ink-1000 hover:bg-brand-deep'
                : 'bg-transparent border-ink-200 text-ink-700 hover:text-ink-1000 hover:border-ink-1000',
            )}
          >
            {Ico.edit}
            <span className="max-[900px]:hidden">Aktivitet</span>
          </button>
        )}
        <ProfileMenu
          me={me}
          adminActive={mode === 'admin'}
          adminDest={mode === 'admin' ? adminDest : null}
          onOpenAdmin={setAdminDest}
        />
      </div>
    </header>
  )
}
